// @flow

import _ from 'lodash';

import startsWith from 'lodash/startsWith';
import isEmpty from 'lodash/isEmpty';
import ArrayUtils from '../utils/ArrayUtils';

import IntlMessageFormat from 'intl-messageformat';

import { addLocaleData, defineMessages } from 'react-intl';

import UrlUtils from '../utils/UrlUtils'

import sdk_de_locale from './locales/de';
import sdk_en_locale from './locales/en';
import sdk_fr_locale from './locales/fr';

import currenciesDE from './data/currencies/de.json';
import currenciesEN from './data/currencies/en.json';
import currenciesFR from './data/currencies/fr.json';

import countriesDE from './data/countries/de.json';
import countriesEN from './data/countries/en.json';
import countriesFR from './data/countries/fr.json';

import languagesDE from './data/languages/de.json';
import languagesEN from './data/languages/en.json';
import languagesFR from './data/languages/fr.json';

import localesDE from './data/locales/de.json';
import localesEN from './data/locales/en.json';
import localesFR from './data/locales/fr.json';

const Currencies = {
  de: currenciesDE,
  en: currenciesEN,
  fr: currenciesFR,
};

const Countries = {
  de: countriesDE,
  en: countriesEN,
  fr: countriesFR,
};

const Languages = {
  de: languagesDE,
  en: languagesEN,
  fr: languagesFR,
};

const Locales = {
  de: localesDE,
  en: localesEN,
  fr: localesFR,
};

/**
 * @Singleton
 *
 * @link https://medium.freecodecamp.com/internationalization-in-react-7264738274a0#.nqpzdmp66
 */
class I18n {

  /**
   * The locale to use by default.
   * This locale is used if the user locale is not available on `localeData`.
   */
  defaultLocale: string = 'en';

  /**
   * Contains the current locale.
   */
  locale: string = '';

  /**
   * Contains all the messages for all the locales.
   */
  localeData: Object = {};

  /**
   * Contains all the messages for the current selected locale.
   */
  messages: Object = {};

  configure(localeData: Object) {
    if (!global.Intl) {
      console.error('Required intl Polyfill');
    }

    this.localeData = localeData;

    // TODO: in dev mode, verify there is no keys missing between languages and fr

    // TODO: in dev mode, verify there is no key conflict between localeData and sdk

    // merge sdk locales with given localeData
    this.localeData.de = _.merge(this.localeData.de, sdk_de_locale);
    this.localeData.en = _.merge(this.localeData.en, sdk_en_locale);
    this.localeData.fr = _.merge(this.localeData.fr, sdk_fr_locale);

    this.changeLocale(this.getDefaultLocale());
  }

  languageWithoutRegionCode(language: string): string {
    if (_.isEmpty(language)) {
      return 'fr';
    }

    // Split locales with a region code
    const languageWithoutRegionCode = language.toLowerCase().split(/[_-]+/)[0];

    return languageWithoutRegionCode;
  }

  getCurrentLanguageWithoutRegionCode(): string {
    return this.languageWithoutRegionCode(this.locale);
  }

  isSupportedLocale(locale) {
    return _.indexOf(_.keys(Locales), locale) !== -1
  }

  getDefaultLocale(): string {
    const l = UrlUtils.getQueryParam('l')
    if (!_.isEmpty(l) && this.isSupportedLocale(l)) {
      return l
    }

    // Define user's language. Different browsers have the user locale defined
    // on different fields on the `navigator` object, so we make sure to account
    // for these different by checking all of them
    const language = (navigator.languages && navigator.languages[0]) ||
      navigator.language ||
      navigator.userLanguage;

    return language;
  }

  changeLocale(locale: string) {
    this.locale = locale;

    // Split locales with a region code
    const languageWithoutRegionCode = this.languageWithoutRegionCode(this.locale);

    // Try full locale, try locale without region code, fallback to the default locale.
    this.messages = this.localeData[languageWithoutRegionCode]
      || this.localeData[this.locale] || this.localeData[this.defaultLocale];

    // TODO: in dev mode, verify all keys exists in differents locales.

    //2025-12 (dependencies upgrade)
    // This defineMessages call is commented out to avoid build errors with react-intl extractor.
    // It doesn't seem necessary to call it here (since changing locale already correctly update messages).
    // IMPORTANT:
    // Do NOT call `defineMessages(this.messages)` here because `defineMessages`
    // must be called with an object literal in source code (object expression)
    // so the react-intl extractor can work. Calling it with a runtime variable
    // (like `this.messages`) causes the build error.
    // If you need extraction, declare your messages as literal objects in source files
    // and call `defineMessages({ ... })` with those literals.
    //defineMessages(this.messages);
  }

  tr(key: string, params: Object = {}): string {
    return this.trWithLocale(this.locale, key, params);
  }

  trWithLocale(locale: string, key: string, params: Object = {}): string {
    if (_.isEmpty(key)) {
      // TODO: in dev, error
      return '';
    }

    const message = this.findForKey(key);

    try {
      const msg = new IntlMessageFormat(message, locale);

      // TODO: handle key does not exists.

      return msg.format(params);
    } catch (e) {
      // Note: Sometimes strings contains params that does not need to be handle here, like
      // components.select.add_label_text `Ajouter {label} ?`
      // Here our IntlMessageFormat.format try to format the label, but don't have any param.
      // For those case, we have to try / catch the error. The tricks to avoid errors consists to
      // set as parameter the same string. Example here:
      // `I18n.tr('components.select.add_label_text', { label: '{label}'})`
      console.warn(`I18n: ${key} error when formatting for locale ${this.locale}`, params);
    }

    return message;
  }

  findForKey(key: string): string {
    const printErrorMessage = (key: string) => {
      // TODO: log error.
      console.warn(`I18n: Impossible to find ${key} for locale ${this.locale}`)
    };

    try {
      const message = this.messages[key];

      if (_.isNil(message)) {
        printErrorMessage(key);
        return this.getDefaultForKey(key);
      }

      return message;
    } catch (e) {
      printErrorMessage(key);
      return this.getDefaultForKey(key);
    }
  }

  getDefaultForKey(key: string): string {
    return key;
    try {
      // we use the 'fr' locale to find default message because it is the most up-to-date language
      // when developing.
      const message = this.localeData['fr'][key];

      if (!_.isNil(message)) {
        return message;
      }
    } catch (e) {
      // just in case, should not happened.
    }

    return key;
  }

  /**
   * Example: flatpickr.init.prototype.l10n = I18n.localizeFlatpickr(flatpickr.init.prototype.l10n)
   *
   * @param flatpickr.init.prototype.l10n
   */
  localizeFlatpickr(l10n: Object): Object {
    // list here https://github.com/cdnjs/cdnjs/tree/master/ajax/libs/flatpickr/1.8.8
    switch (this.getCurrentLanguageWithoutRegionCode().toLowerCase()) {
      case 'de':
        l10n.weekdays = {
          shorthand: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
          longhand: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']
        };

        l10n.months = {
          shorthand: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
          longhand: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
        };
        break;
      case 'fr':
        // Localize. See https://github.com/cdnjs/cdnjs/blob/master/ajax/libs/flatpickr/1.8.8/flatpickr.l10n.fr.js
        l10n.weekdays = {
          shorthand: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
          longhand: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
        };

        l10n.months = {
          shorthand: ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct',
            'Nov', 'Déc'],
          longhand: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août',
            'Septembre', 'Octobre', 'Novembre', 'Décembre']
        };

        l10n.ordinal = (nth) => {
          if (nth > 1) {
            return 'ème';
          }

          return 'er';
        };

        break;
      case 'en':
        break;
      default:
      // TODO: logger
    }

    return l10n;
  }

  getCurrencyName(currencyCode: string): string {
    return Currencies[this.getCurrentLanguageWithoutRegionCode()][currencyCode];
  }

  getCountry(countryCode: string): string {
    return Countries[this.getCurrentLanguageWithoutRegionCode()][countryCode];
  }

  getLanguage(locale: string): string {
    return Languages[this.getCurrentLanguageWithoutRegionCode()][locale];
  }

  getLocale(locale: string): string {
    return Locales[this.getCurrentLanguageWithoutRegionCode()][locale];
  }

  getForCurrentLocale(locales) {
    if (!locales) {
      return ''
    }

    // try to find for current language
    const currentLanguage = this.getCurrentLanguageWithoutRegionCode().toUpperCase()
    let locale = ArrayUtils.find(
      locales,
      locale => locale.locale && startsWith(locale.locale.toLowerCase(), currentLanguage.toLowerCase())
    )

    // try to find for default language
    if (!locale) {
      const defaultLanguage = this.getDefaultLocale()
      .substr(0, 2)
      .toUpperCase();

      if (defaultLanguage !== currentLanguage) {
        locale = ArrayUtils.find(
          locales,
          locale => locale.locale && startsWith(locale.locale.toLowerCase(), defaultLanguage.toLowerCase())
        )
      }
    }

    // none found, fallback to a default
    if (!locale && !isEmpty(locales)) {
      locale = locales[0]
    }

    // avoid crash
    return locale ? locale.text : ''
  }

}

const _I18nInstance = new I18n();

export default _I18nInstance;
